import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiRefreshCw } from "react-icons/fi";
import type { OriginScope } from "@features/report/template/types/reportTemplate";
import type { PresentationLayoutListItem } from "@features/presentationLayout/types/presentationLayout";
import { ORIGIN_SCOPE_LABELS } from "@features/presentationLayout/types/presentationLayout";

type Props = {
  show: boolean;
  layout: PresentationLayoutListItem | null;
  /** Origins the user may create layouts for. */
  availableOriginScopes: OriginScope[];
  busy: boolean;
  onHide: () => void;
  onSave: (origin: OriginScope) => void;
};

export default function ChangePresentationLayoutOriginModal({
  show,
  layout,
  availableOriginScopes,
  busy,
  onHide,
  onSave,
}: Props) {
  const [selected, setSelected] = useState<OriginScope | "">("");

  useEffect(() => {
    setSelected(layout?.origin_scope ?? "");
  }, [layout, show]);

  const options = availableOriginScopes.filter((origin) => origin !== layout?.origin_scope);
  const unchanged = !selected || selected === layout?.origin_scope;

  return (
    <Modal
      show={show}
      onHide={() => !busy && onHide()}
      backdrop="static"
      keyboard={!busy}
      centered
      data-testid="change-layout-origin-modal"
    >
      <Modal.Header closeButton={!busy} className="border-0 pb-0">
        <Modal.Title className="fw-semibold">
          <FiRefreshCw className="me-2" aria-hidden />
          Change report level
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <Alert variant="info" className="mb-3">
          <strong>Current:</strong> {layout ? `${ORIGIN_SCOPE_LABELS[layout.origin_scope]} reports` : "Not set"}
        </Alert>
        <p className="text-muted small">
          Template includes reset to what the new level allows. Rows from templates the new level cannot include
          must be removed first.
        </p>
        {options.length === 0 ? (
          <Alert variant="warning" className="mb-0">No other report levels are available based on your permissions.</Alert>
        ) : (
          <Form.Select
            value={selected}
            disabled={busy}
            onChange={(e) => setSelected(e.target.value as OriginScope | "")}
            aria-label="New report level"
          >
            <option value={layout?.origin_scope ?? ""}>Select a report level…</option>
            {options.map((origin) => (
              <option key={origin} value={origin}>{ORIGIN_SCOPE_LABELS[origin]} reports</option>
            ))}
          </Form.Select>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => selected && onSave(selected)} disabled={busy || unchanged}>
          {busy ? <Spinner animation="border" size="sm" aria-hidden /> : "Save change"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
